import type { IntakeForm } from './intake';
import { BidSnapshot } from './bid';

/**
 * 프로젝트 — 공고에서 골라 우리가 하기로 한 일감. **건 하나가 끝까지 간다.**
 *
 * 2026-09-20 에 구조를 바꿨다. 그전에는 `검토 대상`·`2 재무계획`·`3 제안서`·
 * `4 수행` 이 각각 메뉴였는데, 같은 건을 단계별로 나눠 놓은 것이라 화면마다
 * 같은 항목(프로젝트명·발주기관·제안가·마감·PM)이 되풀이됐다.
 *
 * 이제 **프로젝트 메뉴 하나**고 단계는 보드의 칸이다. 검토는 프로젝트의 첫 단계다.
 *
 * 이 구조가 DB 스키마 초안이다 (mocks/projects.json).
 *
 * ⚠️ **admin 의 `projects` 와 이름이 같지만 뜻이 다르다.** 여기 프로젝트는
 *    검토 단계부터 있고, admin 의 `projects` 는 PPE 를 올린 것만 들어간다.
 *    둘을 잇는 지점은 「PPE 반영」 하나뿐이다.
 */
export interface Project {
  id: number;

  name: string;
  /** 발주기관 */
  clientName: string;
  /** 공고에서 넘어온 것이면 담을 때의 스냅샷. 직접 등록한 프로젝트는 null */
  bid: BidSnapshot | null;

  stage: ProjectStage;
  /** 제안가(원). 재무계획에서 잡는다 */
  proposalPrice: number | null;
  /** 제안서 마감 또는 개찰 일시 */
  dueAt: string;
  pmName: string;
  /** 검토 메모 */
  memo: string;

  /* --- 수주 판정 --- */
  /** 아직 판정 전이면 null */
  outcome: Outcome | null;
  decidedAt: string | null;
  /** 계약금액(원). 제안가와 다를 수 있다 */
  contractAmount: number | null;
  /** 나라장터 결과 조회(공고 건) — state 대기 · 수주 · 실주, 낙찰자 · 금액 · ppe 자동 반영 결과 */
  bidResult?: BidResult | null;

  /**
   * admin `projects`·`project_ppe` 에 반영한 시각. 반영 전이면 null.
   *
   * **자동으로 하지 않는다.** 사람이 「PPE 반영」을 눌러야 간다(2026-09-20 결정).
   * 2026-09-28 부터 수주 전에도 누를 수 있다 — admin 은 계약 전에 PPE 부터 올린다.
   */
  ppeRegisteredAt: string | null;
  /** 이어진 admin 프로젝트 번호 */
  adminProjectId?: number | null;
  /** 사업 접수서 — 「프로젝트 추가」 로 만든 건만(2026-10-22) */
  intake?: IntakeForm | null;

  createdAt: string;
}

/**
 * 「PPE 반영」 입력 — admin 에 이미 있으면 번호로 잇고, 없으면 새로 만든다.
 * 금액은 admin 단위인 **만원**이다(여기 계약금액은 원).
 */
export type PpeInput =
  | { adminProjectId: number }
  | { startDate: string; endDate: string; pm: string; contractValue: number };

export const OUTCOMES = ['수주', '실주'] as const;
export type Outcome = (typeof OUTCOMES)[number];

/** 프로젝트가 지나가는 단계. 보드의 칸과 같다 */
export const PROJECT_STAGES = ['검토', '재무계획', '제안서', '수행', '종료'] as const;
export type ProjectStage = (typeof PROJECT_STAGES)[number];

export const STAGE_STYLE: Record<ProjectStage, string> = {
  '검토': 'bg-slate-100 text-slate-600',
  '재무계획': 'bg-indigo-50 text-indigo-600',
  '제안서': 'bg-amber-50 text-amber-700',
  '수행': 'bg-emerald-50 text-emerald-700',
  '종료': 'bg-slate-100 text-slate-400',
};

/**
 * 프로젝트 상세의 흐름표. 탭이 아니라 **지나온 길**이다.
 *
 * `gate`(수주 판정)는 단계가 아니라 갈림길이라 보드에는 칸이 없다.
 * 여기서만 admin 으로 넘어간다.
 */
export const PROJECT_STEPS = [
  { key: 'bid', label: '공고', stage: '검토' },
  { key: 'review', label: '검토', stage: '검토' },
  { key: 'plan', label: '재무계획', stage: '재무계획' },
  { key: 'proposal', label: '제안서', stage: '제안서' },
  { key: 'gate', label: '수주 판정', stage: '제안서' },
  { key: 'output', label: '산출물', stage: '종료' },
] as const;
export type ProjectStepKey = (typeof PROJECT_STEPS)[number]['key'];

/** 지금 단계에서 기본으로 열 흐름표 칸 */
export const STAGE_TO_STEP: Record<ProjectStage, ProjectStepKey> = {
  '검토': 'review',
  '재무계획': 'plan',
  '제안서': 'proposal',
  // 수행 칸은 2026-10-16 에 뺐다 — 수주한 건(보드 「수행」)은 산출물 칸을 연다
  '수행': 'output',
  '종료': 'output',
};

/** 이 프로젝트가 어디까지 왔나 — 흐름표에서 체크로 그릴 칸 수 */
export function reachedIndex(b: Project): number {
  if (b.outcome) return PROJECT_STEPS.length - 1;
  return PROJECT_STEPS.findIndex((s) => s.key === STAGE_TO_STEP[b.stage]);
}

/**
 * 재무계획 — 투입 인력과 원가를 잡는다.
 *
 * ⚠️ **확정해도 admin 에 가지 않는다** (2026-09-20 변경). 제안 원가안이라 자주
 *    바뀌고 실주할 수도 있어서, 실주한 건이 admin 수익성에 섞이면 안 된다.
 *    admin 반영은 사람이 「PPE 반영」을 눌렀을 때만 일어난다.
 *
 * ⚠️ 그때도 **직접 INSERT 하지 않는다.** `project_ppe` 에 트리거 둘이
 *    INSERT·UPDATE·DELETE 모두에 걸려 있다 (`cr_update_trigger`,
 *    `trigger_sync_ppe_data` — 2026-09-20 개발·운영 확인). 반드시 admin API 를
 *    거친다. 이후 수익성·CR·CashFlow 는 admin 이 정본이다.
 */
export interface FinancePlan {
  projectId: number;
  manpower: ManpowerRow[];
  /** 직접경비 */
  expenses: { travel: number; outsourcing: number; etc: number };
  /** 제안가(원) */
  proposalPrice: number;
  status: '임시저장' | '확정';
  confirmedAt: string | null;
  /** PPE 모양 재무계획(만원). 옛 계획은 null — 화면이 제안가로 빈 PPE 를 만든다 */
  ppe: Ppe | null;
}

/**
 * PPE 모양 재무계획 — admin PPE 「새 프로젝트 등록」(NewProjectModal)과 같은 줄 · 같은 단위(**만원**) · 같은 셈(2026-10-16).
 * 나중에 「PPE 반영」 때 그대로 넘길 수 있게 admin `project_ppe` json 칸 모양을 따른다.
 */
export interface PpeItem { name: string; amount: number }
export interface Ppe {
  /** 사업 기간 — 인력원가 연도(시작 연도)와 개월 수가 여기서 나온다 */
  startDate: string;
  endDate: string;
  months: number;
  /** Revenue by Schedule — 착수금 · 중도금 · 잔금. 금액을 넣고 합계가 Revenue. ratio 는 0~1(자동) */
  revenueItems: (PpeItem & { ratio: number; dueDate: string })[];
  /** 투입인건비 — 사람 · M/M · 인력원가(만원/MM) */
  manpowerItems: { name: string; employeeId: number | null; position?: string; mm: number; unitCost: number }[];
  /** CoGs — Sales Brokerage Fee(금액) · 프리랜서 고급 · 중급(M/M × 단가) */
  grossCostItems: (PpeItem & { unitPrice?: number; mm?: number })[];
  /** Indirect Opex — Rent & Overhead · Cost of Finance(투입인건비 × 요율, 자동) */
  indirectOpexItems: PpeItem[];
  /** Direct Opex — Project management(매출 × 요율, 자동) · ENS · Print & Paper · Conference · Travel for business */
  directOpexItems: PpeItem[];
  costOfFinanceRate: number;
  projectManagementRate: number;
  /** 인력원가를 읽은 해 */
  hrYear: number;
}

/** 인력원가 한 사람(만원/MM) — admin PPE 와 같은 값 */
export interface HrCost { employeeId: number; name: string; position: string; status: string; unitCost: number }

export interface ManpowerRow {
  id: number;
  /** 직급 */
  grade: string;
  /** 인원 */
  headcount: number;
  /** 투입 인월 */
  monthsPerPerson: number;
  /** 월 단가(원) */
  monthlyRate: number;
}

/** 시안에 나오는 직급. admin 의 직급 체계를 따라야 하므로 잠정이다 */
export const GRADES = [
  'Partner',
  'Principal',
  'Senior Project Analyst',
  'Project Analyst',
  'Analyst',
] as const;

export const rowAmount = (r: ManpowerRow): number =>
  Math.round(r.headcount * r.monthsPerPerson * r.monthlyRate);

/** 나라장터 결과 — 낙찰정보 · 개찰결과 조회(2026-10-16) */
export interface BidResult {
  state?: '대기' | '수주' | '실주';
  progress?: string;
  openedAt?: string | null;
  winner?: string;
  winnerBizNo?: string;
  amount?: number | null;
  finalDate?: string | null;
  participants?: number | null;
  checkedAt?: string;
  checkedBy?: string;
  ppe?: { state: string; missing?: string[]; adminProjectId?: number; error?: string; detail?: string | null };
  /** 사람이 판정한 기록(공고 건, 2026-11-02) — 나라장터 결과와 견준다 */
  manual?: { outcome: '수주' | '실주'; by: string; at: string; amount: number | null };
}
